import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { HttpResponse } from 'shell/models/http-response';
import { useFormik } from 'formik';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Card, CardContent, Grid, IconButton, Typography } from '@mui/material';
import { FiltersFormFamily } from 'family/view-models/family-list';
import { headCells } from 'mutation/constants/family-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformFamilyMembers } from 'mutation/utils/transform-family-members';
import { noop } from 'shell/utils/noop';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { UpdateParenthoodVM } from 'mutation/view-models/update-parenthood';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import { Replay, Save } from '@mui/icons-material';
import { UpdateParenthoodCrumbs } from 'mutation/constants/update-parenthood-crumbs';
import { transformMutationUpdateParenthood } from 'mutation/utils/transformer-request-update-parenthood';
import { toast } from 'react-toastify';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import FilterFamily from 'family/components/filters';
import { transformSearchFamilyDatasetFull } from 'shell/utils/transform-dataset-filter-list';
import { initialValuesFamily } from 'family/pages';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { validationSchemaFiltersFamily } from 'family/constants/family-list';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import * as yup from 'yup';

const UpdateParenthoodPage = (): React.ReactElement => {
  const [familyMembers, getAnpr] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AnprVM<UpdateParenthoodVM>
  >(`${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: [],
    },
    transformer: transformFamilyMembers,
  });

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [visibilityResponse, setVisibilityResponse] = useState<{
    error?: boolean;
    warning?: boolean;
  }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibilityResponse = useCallback(
    (openV, type) => {
      const visibilityMessage = type === 'error' && !openV ? false : openV;

      setVisibilityResponse((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibilityResponse]
  );

  const onSubmit = useCallback(
    (formValues: FiltersFormFamily) => {
      filterFamilyForm.setFieldValue('page', 0);

      const data = transformSearchFamilyDatasetFull({ ...formValues, page: 0 });

      (
        getAnpr({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<UpdateParenthoodVM>>
      ).then(() => setVisibility({ error: undefined, warning: undefined }));
    },
    [getAnpr, setVisibility]
  );

  const { userProfile } = useUserProfile();

  const filterFamilyForm = useFormik<FiltersFormFamily>({
    initialValues: {
      ...initialValuesFamily,
      subjectData: {
        ...initialValuesFamily?.subjectData,
        name: '',
        surname: '',
        withoutName: false,
        withoutSurname: false,
        istatCode: '',
        anprId: '',
        municipalityId: '',
      },
      municipalityOfResidence: {
        id: userProfile.datiLogin?.codiceIstat,
        description: userProfile.datiLogin?.nomeComune || '',
        province: userProfile.datiLogin?.siglaProvincia || '',
      },
    },
    validationSchema: validationSchemaFiltersFamily,
    onSubmit,
    onReset: () => {
      setState(undefined);
      onVisibility(false, 'warning');
      onVisibility(false, 'error');
    },
  });

  const response = familyMembers.data as AnprVM<UpdateParenthoodVM>;

  const [state, setState] = useState<Array<UpdateParenthoodVM> | undefined>(response?.persons);

  useEffect(() => {
    setState(response?.persons);
  }, [response?.persons]);

  const [responseData, updateParenthood] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/famiglia-convivenza/aggiornamento-parentela`, {
    onBootstrap: false,
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseData.data as SaveDataAnpr;

  const onChangePage = useCallback(
    (page) => {
      filterFamilyForm.setFieldValue('page', page || 0);
    },
    [filterFamilyForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterFamilyForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      filterFamilyForm.setValues(newDataset);
    },
    [filterFamilyForm.setValues, filterFamilyForm.values]
  );

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (response?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title = `Ci sono ${response?.errors?.length} errori`;
      errorToVisualize.messages = response?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, response]);

  const errorMessageResponse = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedSavedData?.errors &&
      castedSavedData?.errors?.length > 0 &&
      visibilityResponse.error === undefined
    ) {
      errorToVisualize.title = `Ci sono ${castedSavedData?.errors?.length} errori`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibilityResponse.error;

    return errorToVisualize;
  }, [visibilityResponse.error, castedSavedData]);

  const formList = useFormik<{
    controlDate?: DateTime | null;
  }>({
    initialValues: {
      controlDate: DateTime.now(),
    },
    validationSchema: yup.object().shape({
      controlDate: yup.date().required('Campo obbligatorio').typeError('Data errata'),
    }),
    onSubmit: () => {
      launchUpdate();
    },
  });

  const launchUpdate = useCallback(() => {
    const data = transformMutationUpdateParenthood(state || [], formList.values.controlDate);
    (
      updateParenthood({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((responseUpdate) => {
      const castedResponse = responseUpdate as SaveDataAnpr;
      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }
      toast.success('Operazione eseguita con successo');
    });
  }, [updateParenthood, state, formList]);

  const body: Array<UpdateParenthoodVM> = useMemo(() => {
    return (state || []).map((item) => {
      return {
        ...item,
        relationshipSelect: (
          <ParenthoodRelationshipList
            isHeadParenthood={
              !!state?.find((item1) => {
                if (item1.fiscalCode !== item.fiscalCode) {
                  return item1.relationship?.toString() === '1';
                }
                return false;
              })
            }
            value={item.relationship || ''}
            onChange={(value) => {
              const mappedElements = state?.map((item1) =>
                item1.fiscalCode === item.fiscalCode ? { ...item1, relationship: value } : item1
              );
              setState(mappedElements);
            }}
          />
        ),
        action: (
          <>
            <IconButton
              aria-label="Salva"
              aria-haspopup="true"
              onClick={() => {
                formList.submitForm();
              }}
              style={{
                padding: '4px',
              }}
            >
              <Save />
            </IconButton>
            <IconButton
              aria-label="Reset"
              aria-haspopup="true"
              onClick={() => {
                const mappedElements = state?.map((item1) =>
                  item1.fiscalCode === item.fiscalCode
                    ? { ...item1, relationship: item.relationshipOld }
                    : item1
                );
                setState(mappedElements);
              }}
              style={{
                padding: '4px',
              }}
            >
              <Replay />
            </IconButton>
          </>
        ),
      };
    });
  }, [state, setState, formList.handleSubmit]);

  return (
    <Fragment>
      <Breadcrumbs items={UpdateParenthoodCrumbs} />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Ricerca Famiglia
      </Typography>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          response?.warnings?.length > 1
            ? `Ci sono ${response?.warnings?.length} warning`
            : `C'è 1 warning`
        }
        messages={response?.warnings}
        visibility={
          visibility.warning !== undefined ? visibility.warning : response?.warnings?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Error}
        title={errorMessageResponse.title || ''}
        messages={errorMessageResponse.messages || []}
        visibility={errorMessageResponse.visibility || false}
        onChangeVisibility={onVisibilityResponse}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          castedSavedData?.warnings && castedSavedData?.warnings?.length > 1
            ? `Ci sono ${response?.warnings?.length} warning`
            : `C'è 1 warning`
        }
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : !!castedSavedData?.warnings && castedSavedData?.warnings?.length > 0
        }
        onChangeVisibility={onVisibilityResponse}
      />
      <div className="pmi-my-3">
        <FilterFamily filterFamilyForm={filterFamilyForm} gobackUrl="/mutazioni" />
      </div>
      {response && state ? (
        body.length > 0 ? (
          <Fragment>
            <Card raised>
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  Aggiornamento Parentela
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Data decorrenza *"
                      aria-label="Data decorrenza *"
                      value={formList.values?.controlDate || null}
                      onChange={(date) => {
                        formList.setFieldValue('controlDate', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField
                          {...params}
                          id="controlDate"
                          fullWidth
                          error={
                            formList.touched?.controlDate && Boolean(formList.errors?.controlDate)
                          }
                          helperText={formList.errors?.controlDate}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography gutterBottom variant="h6" component="div">
                      Lista Membri
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Table
                      ariaLabel="Lista membri famiglia"
                      page={filterFamilyForm.values.page}
                      rowsPerPage={filterFamilyForm.values.rowsPerPage}
                      body={body}
                      count={((response?.persons as Array<UpdateParenthoodVM>) || []).length}
                      head={headCells}
                      onSort={noop}
                      onPageChange={onChangePage}
                      onRowsPerPageChange={onChangeRowsPerPage}
                      onVisibilityPress={noop}
                      orders={filterFamilyForm.values.orders}
                      isNotLayoutFixed
                      noSort
                      hideButtons
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Fragment>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

UpdateParenthoodPage.displayName = 'Update Parenthood page component';

export default UpdateParenthoodPage;
